import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import ApplianceStep from "@/components/solar/ApplianceStep";
import PlanResults from "@/components/energy/PlanResults";
import { ToolShell, NumInput, TextInput, SelectInput, ErrorBox } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

export default function EnergyEngine() {
  const [config, setConfig] = useState(null);
  const [profile, setProfile] = useState({ location: "", property_type: "home", battery_chemistry: "lithium", distances_m: { solar: 10, battery: 2, ac: 5 } });
  const [goal, setGoal] = useState("whole_property");
  const [items, setItems] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/energy/config").then((r) => setConfig(r.data)).catch(() => setError("Could not load the Energy Engine. Please refresh."));
  }, []);

  const compute = async () => {
    if (!items.length) { setError("Please add at least one appliance before continuing."); return; }
    const bad = items.find((i) => !(Number(i.qty) >= 1) || !(Number(i.watts) > 0) || !(Number(i.hours) >= 0) || Number(i.hours) > 24);
    if (bad) { setError(`Check the values for "${bad.name}" — quantities, watts and hours must be positive (hours ≤ 24).`); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/energy/plan", { profile, appliances: items, goal });
      setResult({ plan: data.plan, items: data.items, profile });
      setTimeout(() => document.getElementById("engine-results")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="BNM Energy Engine™ — Energy System Planner" description="Tell us what you need to power. The BNM Energy Engine estimates the solar, battery, inverter and protection system required — with transparent assumptions and SLE pricing." />
      <ToolShell
        testid="energy-engine"
        eyebrow="BNM Energy Engine™"
        title="Tell us what you need to power."
        lead="We'll help you understand the energy system required — solar, batteries, inverter, cables and protection, with estimated costs in Leones. Preliminary engineering assessment; final sizing is verified on site."
      >
        {error && <div className="mb-6"><ErrorBox msg={error} /></div>}
        {!config ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="space-y-8">
            <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold text-slate-900">1. Your property</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <TextInput label="Location / community" testid="engine-location" value={profile.location}
                  onChange={(v) => setProfile((p) => ({ ...p, location: v }))} />
                <SelectInput label="Property type" testid="engine-property-type" value={profile.property_type}
                  onChange={(v) => setProfile((p) => ({ ...p, property_type: v }))}
                  options={config.property_types.map((t) => ({ value: t, label: t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }))} />
                <SelectInput label="System goal" testid="engine-goal" value={goal} onChange={setGoal}
                  options={Object.entries(config.goals).map(([value, label]) => ({ value, label }))} />
                <SelectInput label="Battery chemistry" testid="engine-chemistry" value={profile.battery_chemistry}
                  onChange={(v) => setProfile((p) => ({ ...p, battery_chemistry: v }))}
                  options={[{ value: "lithium", label: "Lithium / LiFePO4 (recommended)" }, { value: "leadacid", label: "Lead-acid" }]} />
              </div>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Cable distances (approximate)</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[["solar", "Panels → inverter (m)"], ["battery", "Battery → inverter (m)"], ["ac", "Inverter → board (m)"]].map(([k, label]) => (
                  <NumInput key={k} label={label} min={0.5} max={300} testid={`engine-dist-${k}`} value={profile.distances_m[k]}
                    onChange={(v) => setProfile((p) => ({ ...p, distances_m: { ...p.distances_m, [k]: v } }))} />
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold text-slate-900">2. Your appliances</h2>
              <p className="mt-1 text-sm text-slate-500">Add everything you want to power. Mark daytime or nighttime use — it shapes your battery sizing.</p>
              <div className="mt-5">
                <ApplianceStep library={config.appliances} items={items} setItems={setItems} showPeriod />
              </div>
            </div>

            <button onClick={compute} disabled={busy} data-testid="engine-compute"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-obsidian px-8 py-4 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt disabled:opacity-60">
              {busy ? "Calculating…" : "Build My Energy Plan"}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {result && (
          <div id="engine-results" className="mt-4 scroll-mt-28">
            <PlanResults plan={result.plan} items={result.items} profile={result.profile} />
          </div>
        )}
      </ToolShell>
    </>
  );
}
